import { FieldStatistics } from '../providers/field-statistics.service';

export class Utils {
  public static roundDown(val, decimal = 1) {
    if (val < 0) {
      return Math.ceil(val * Math.pow(10, decimal)) / Math.pow(10, decimal);
    } else {
      return Math.floor(val * Math.pow(10, decimal)) / Math.pow(10, decimal);
    }
  }

  public static roundHalfAway(val, decimal = 1) {
    return (
      (Math.sign(val) * Math.floor(Math.abs(val * Math.pow(10, decimal)) + 0.5)) /
      Math.pow(10, decimal)
    );
  }

  public static roundNearest(val) {
    return +val.toFixed(1);
  }

  public static round(val, decimal = 1) {
    return Math.round(val * Math.pow(10, decimal)) / Math.pow(10, decimal);
  }

  public static floor(val, decimal = 0) {
    return Math.floor(val * Math.pow(10, decimal)) / Math.pow(10, decimal);
  }

  public static compact(val, intPrecision) {
    return Math.round(val / Math.pow(10, intPrecision)) * Math.pow(10, intPrecision);
  }

  public static truncate(value: string, limit: number = 20, separator: string = '…'): string {
    if (!value) {
      return '';
    }
    if (value.length <= limit + 1) {
      return value;
    }
    const head = value.substr(0, limit / 2);
    const tail = value.substr(value.length - limit / 2);
    return head + separator + tail;
  }

  public static truncateEnd(value: string, limit: number = 20, separator: string = '…'): string {
    if (!value) {
      return '';
    }
    if (value.length <= limit + 1) {
      return value;
    }
    const text = value.substring(0, limit);
    return text + separator;
  }

  public static capitalize(value: string): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  public static addThousandSeparator(number: number | string) {
    if (number === undefined || number === null) {
      return '';
    }

    if (typeof number === 'number') {
      number = number.toString();
    }

    const [integerPart, decimalPart] = number.split('.');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
  }

  public static deepEquals(o1: any, o2: any, propsToSkip: string[] = []): boolean {
    let ret = true;

    const compareNull = (o) => {
      let equals = o === null || o === undefined;
      if (typeof o === 'boolean') {
        equals = o === false;
      } else if (typeof o === 'string') {
        equals = o === '';
      } else if (Array.isArray(o)) {
        equals = (o as Array<any>).length === 0;
      }
      return equals;
    };

    if (o1 === null || o1 === undefined) {
      ret = compareNull(o2);
    } else if (o2 === null || o2 === undefined) {
      ret = compareNull(o1);
    } else if (typeof o1 === 'object' || typeof o2 === 'object') {
      if (o1 instanceof Date || o2 instanceof Date) {
        const o1Date = typeof o1 === 'string' ? new Date(o1) : o1;
        const o2Date = typeof o2 === 'string' ? new Date(o2) : o2;

        ret = o1Date.getTime() === o2Date.getTime();
      } else if (Array.isArray(o1)) {
        if (Array.isArray(o2)) {
          if (o1.length !== o2.length) {
            ret = false;
          } else {
            (o1 as Array<any>).forEach(
              (v, i) => (ret = ret && Utils.deepEquals(v, o2[i], propsToSkip))
            );
          }
        } else if (o2 === null || o2 === undefined) {
          ret = (o1 as Array<any>).length === 0;
        } else {
          ret = false;
        }
      } else {
        const o1Keys = Object.keys(o1).filter((k) => !propsToSkip.includes(k));
        const o2Keys = Object.keys(o2).filter((k) => !propsToSkip.includes(k));

        if (
          o1Keys
            .filter((k) => o1[k] !== null && o1[k] !== undefined)
            .some((k) => !o2Keys.includes(k))
        ) {
          ret = false;
        } else if (
          o2Keys
            .filter((k) => o2[k] !== null && o2[k] !== undefined)
            .some((k) => !o1Keys.includes(k))
        ) {
          ret = false;
        } else {
          Object.keys(o1)
            .filter((k) => !propsToSkip.includes(k))
            .forEach((k) => {
              ret = ret && Utils.deepEquals(o1[k], o2[k], propsToSkip);
            });
        }
      }
    } else {
      ret = ret && o1 === o2;
    }

    return ret;
  }

  public static areEqualOrFalsy(a: any, b: any) {
    return a === b || (!a && !b);
  }

  //Because why would javascript have a built in array comparison
  public static compareStringArrayExactOrder(arr1: Array<string>, arr2: Array<string>) {
    if (!arr1 || !arr2) {
      return false;
    }
    if (arr1.length === 0 && arr2.length === 0) {
      return true;
    }
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }

  public static compareStringArray(arr1: Array<string>, arr2: Array<string>) {
    return this.compareStringArrayExactOrder([...arr1].sort(), [...arr2].sort());
  }

  public static replaceNonPrintable(source) {
    if (source) {
      return source.replace(/[\x01-\x09\x0B-\x0C\x0E-\x20]/g, ' ');
    }
    return source;
  }

  public static titleCase(str) {
    if (!str) {
      return '';
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public static toUpperSnakeCase(value: string): string {
    if (!value) {
      return '';
    }
    const normalizedValue: string = value
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    return normalizedValue.toUpperCase();
  }

  public static escapeHtml(str: string) {
    if (str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
    return str;
  }

  public static unescapeBlank(str: string) {
    if (str === '&lt;blank&gt;') {
      return this.unescapeHtml(str);
    }
    return str;
  }

  public static unescapeHtml(str: string) {
    if (str) {
      return str
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
    return str;
  }

  public static fauxEscapeHtml(str: string) {
    if (!str) {
      return str;
    }
    return str.replace(
      /[&<>'"]/g,
      (tag) =>
        ({
          '&': 'XXampYY',
          '<': 'XXltYY',
          '>': 'XXgtYY',
          "'": 'XX#39YY',
          '"': 'XXquotYY',
        })[tag] || tag
    );
  }

  public static fauxUnescapeHtml(str: string) {
    if (!str) {
      return str;
    }
    return str.replace(
      /XXampYY|XXltYY|XXgtYY|XX#39YY|XXquotYY/g,
      (tag) =>
        ({
          XXampYY: '&',
          XXltYY: '<',
          XXgtYY: '>',
          'XX#39YY': "'",
          XXquotYY: '"',
        })[tag] || tag
    );
  }

  static escapeCsvString(str: string, escapeComma = true) {
    let result = str.replace(/"/g, '""');

    if (escapeComma && result.includes(',')) {
      result = `"${result}"`;
    }

    return result;
  }

  public static deepCloneWithNulls(obj) {
    return JSON.parse(JSON.stringify(obj, (_, v) => (v === undefined ? null : v)));
  }

  public static stringifyRecordValues(record: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(record).map(([key, value]) => {
        if (typeof value === 'string') {
          return [key, value];
        }
        try {
          return [key, JSON.stringify(value)];
        } catch {
          // fallback for unstringifiable values (e.g., circular refs)
          return [key, String(value)];
        }
      })
    );
  }

  public static getDateIsoString(date: Date) {
    const str = new Date(date.toDateString()).toISOString();
    return str;
  }

  public static toHoursMinutesSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    let result = `${minutes.toString().padStart(1, '0')}:${seconds.toString().padStart(2, '0')}`;

    if (!!hours) {
      result = `${hours.toString()}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    } else if (minutes === 0) {
      result = `0:${seconds.toString().padStart(2, '0')}`;
    }

    return result;
  }

  public static mergeArrays<T>(
    aArr: T[] = [],
    bArr: T[] = [],
    predicate = (a: T, b: T) => a === b
  ): T[] {
    const result = [...aArr];

    for (const bi of bArr) {
      if (result.some((ri) => predicate(bi, ri))) {
        continue;
      }

      result.push(bi);
    }

    return result;
  }

  // https://stackoverflow.com/a/8076436/3498008
  static hashCode(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      const code = string.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + code;
      // eslint-disable-next-line no-bitwise
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  static sum(arr: number[]): number {
    return arr.reduce((total, aNumber) => total + aNumber, 0);
  }

  static average(arr: number[]): number {
    return this.sum(arr) / arr.length;
  }

  static median(arr: number[]): number {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    }

    return sorted[mid];
  }

  static conversionRateFor(
    fieldName: string,
    fieldStatistics: { [field: string]: FieldStatistics }
  ): number {
    const stats = fieldStatistics[fieldName];
    const conversion = 1.0 - stats.missing / (stats.missing + stats.count);
    return conversion * 100;
  }

  /**
   * Determine if a color is a light color
   * @see https://gist.github.com/krabs-github/ec56e4f1c12cddf86ae9c551aa9d9e04
   */
  static isLightColor(color: string): boolean {
    let r, g, b;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
      // If HEX --> store the red, green, blue values in separate variables
      const rgb = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

      r = rgb[1];
      g = rgb[2];
      b = rgb[3];
    } else {
      // If RGB --> Convert it to HEX: http://gist.github.com/983661
      const hexColor = +('0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));

      r = hexColor >> 16;
      g = (hexColor >> 8) & 255;
      b = hexColor & 255;
    }

    // HSP equation from http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    // > 127.5 is 'light', <= 127.5 is 'dark'

    return hsp > 127.5;
  }
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name;
